import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { PropTypes } from "prop-types";
import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Tooltipable from "~/components/effects/tooltipable";
import ErrorMessage from "~/components/forms/ErrorMessage";
import { NOOP } from "~/constants/callbacks";

const MAX_TITLE_LENGTH = 100;

const SHARE_TOOLTIP =
  "Shared templates are visible to all users in your account but can only be edited by you. Sharing is only available to admin users. Keep titles short — the creator's name is appended in the picker.";

const TEMPLATE_TYPES = ["email", "sms", "appointment", "task", "note"];

const TEMPLATE_LABELS = {
  email: "Email",
  sms: "Text",
  appointment: "Appointment",
  task: "Task",
  note: "Note",
};

const CreateTemplateModal = ({
  templateType = "email",
  show = false,
  onHide = NOOP,
  onCreate = NOOP,
  isCreating = false,
  isAdmin = false,
  formValues = {},
}) => {
  const [title, setTitle] = useState("");
  const [shared, setShared] = useState(false);
  const [errors, setErrors] = useState({});

  const typeLabel = TEMPLATE_LABELS[templateType] || "Template";

  const resetFields = useCallback(() => {
    setTitle("");
    setShared(false);
    setErrors({});
  }, []);

  useEffect(() => {
    if (!show) {
      resetFields();
    }
  }, [show, resetFields]);

  const validate = useCallback(() => {
    const newErrors = {};

    if (!title.trim()) {
      newErrors.title = ["Title can't be blank"];
    } else if (title.length > MAX_TITLE_LENGTH) {
      newErrors.title = [
        `Title is too long (maximum ${MAX_TITLE_LENGTH} characters).`,
      ];
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [title]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (isCreating) {
        return;
      }
      if (!validate()) {
        return;
      }

      let payload = { title: title.trim(), shared: isAdmin ? shared : false };

      switch (templateType) {
        case "email":
          payload = {
            ...payload,
            subject: formValues.subject || "",
            body: formValues.body || "",
            attachment_ids: (formValues.attachments || []).map((a) => a.id),
          };
          break;

        case "sms":
          payload = {
            ...payload,
            body: formValues.body || "",
            media_url: formValues.media_url || "",
            media_name: formValues.media_name || "",
          };
          break;

        case "appointment": {
          const dur = parseInt(formValues.duration, 10);
          payload = {
            ...payload,
            subject: formValues.subject || "",
            body: formValues.body || "",
            duration: Number.isNaN(dur) ? null : dur,
            time: formValues.time || "",
            timezone: formValues.timezone || "",
            location: formValues.location || "",
          };
          break;
        }

        case "task":
          payload = {
            ...payload,
            name: formValues.name || "",
            description: formValues.description || "",
            category: formValues.category || "follow_up",
            priority: formValues.priority || "normal",
            owner_id: formValues.owner_id || null,
            status: formValues.status || "",
          };
          break;

        case "note":
          payload = {
            ...payload,
            body: formValues.body || "",
          };
          break;

        default:
          break;
      }

      onCreate(payload);
    },
    [
      isCreating,
      title,
      isAdmin,
      shared,
      templateType,
      formValues,
      onCreate,
      validate,
    ],
  );

  const handleHide = useCallback(() => {
    resetFields();
    onHide();
  }, [resetFields, onHide]);

  return (
    <Modal
      show={show}
      onHide={handleHide}
      keyboard={false}
      size="sm"
      enforceFocus={false}
      backdrop="static"
      className="create-template-modal-overlay"
      backdropClassName="create-template-modal-backdrop"
    >
      <Modal.Header closeButton>
        <Modal.Title>{`Create a new ${typeLabel} Template`}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Title field — all types */}
        <div className="form-group">
          <label htmlFor="template_title" className="label">
            Title
          </label>
          <input
            id="template_title"
            name="template[title]"
            placeholder="Template name (shown in picker)"
            className={classNames("form-control", {
              "has-error": errors.title,
            })}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={MAX_TITLE_LENGTH}
          />
          {errors.title && <ErrorMessage message={errors.title} />}
        </div>

        {/* Share checkbox — admin only */}
        {isAdmin && (
          <div className="form-group">
            <div className="custom-control custom-checkbox">
              <input
                id="template_shared"
                type="checkbox"
                className="custom-control-input"
                checked={shared}
                onChange={(e) => setShared(e.target.checked)}
              />
              <label className="custom-control-label" htmlFor="template_shared">
                Share template
                {/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: prevents label checkbox toggle on tooltip click */}
                <span onClick={(e) => e.preventDefault()}>
                  <Tooltipable
                    text={SHARE_TOOLTIP}
                    trigger="click"
                    className="ml5"
                  >
                    <FontAwesomeIcon
                      icon={["fal", "fa-info-circle"]}
                      className="text-secondary"
                      style={{ fontSize: "0.75em" }}
                    />
                  </Tooltipable>
                </span>
              </label>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleHide}
          disabled={isCreating}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={isCreating}
          onClick={handleSubmit}
        >
          {isCreating ? (
            <>
              <FontAwesomeIcon icon={["far", "spinner"]} pulse /> Saving...
            </>
          ) : (
            "Save"
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

CreateTemplateModal.propTypes = {
  templateType: PropTypes.oneOf(TEMPLATE_TYPES),
  show: PropTypes.bool,
  onHide: PropTypes.func,
  onCreate: PropTypes.func,
  isCreating: PropTypes.bool,
  isAdmin: PropTypes.bool,
  formValues: PropTypes.shape({
    body: PropTypes.string,
    attachments: PropTypes.arrayOf(PropTypes.shape({})),
    media_url: PropTypes.string,
    media_name: PropTypes.string,
    subject: PropTypes.string,
    duration: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    time: PropTypes.string,
    timezone: PropTypes.string,
    location: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    category: PropTypes.string,
    priority: PropTypes.string,
    owner_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    status: PropTypes.string,
  }),
};

export default CreateTemplateModal;
